import { useEffect, useRef } from "react";
import { Color, Mesh, OrthographicCamera, Scene, ShaderMaterial, SphereGeometry, WebGLRenderer } from "three";

const vertexShader = `
  varying vec3 surface;
  varying vec3 surfaceNormal;
  void main() {
    surface = position;
    surfaceNormal = normalize(normalMatrix * normal);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = `
  uniform float elapsed;
  uniform float highlight;
  uniform float stormEnabled;
  uniform vec3 accent;
  uniform vec3 foreground;
  varying vec3 surface;
  varying vec3 surfaceNormal;

  float hash(vec3 point) {
    point = fract(point * 0.3183099 + vec3(0.17, 0.31, 0.53));
    point *= 17.0;
    return fract(point.x * point.y * point.z * (point.x + point.y + point.z));
  }

  float noise(vec3 point) {
    vec3 cell = floor(point);
    vec3 blend = fract(point);
    blend = blend * blend * (3.0 - 2.0 * blend);
    return mix(
      mix(mix(hash(cell), hash(cell + vec3(1, 0, 0)), blend.x),
          mix(hash(cell + vec3(0, 1, 0)), hash(cell + vec3(1, 1, 0)), blend.x), blend.y),
      mix(mix(hash(cell + vec3(0, 0, 1)), hash(cell + vec3(1, 0, 1)), blend.x),
          mix(hash(cell + vec3(0, 1, 1)), hash(cell + vec3(1, 1, 1)), blend.x), blend.y), blend.z);
  }

  float turbulence(vec3 point) {
    float total = 0.0;
    float amplitude = 0.55;
    for (int octave = 0; octave < 4; octave++) {
      total += amplitude * noise(point);
      point = point * 2.03 + vec3(7.1, 3.7, 1.3);
      amplitude *= 0.48;
    }
    return total;
  }

  float lightningPath(float vertical, float seed) {
    float segment = (vertical + 1.0) * 10.0;
    float cell = floor(segment);
    float bend = mix(hash(vec3(cell, seed, 0.0)), hash(vec3(cell + 1.0, seed, 0.0)), fract(segment));
    return (bend - 0.5) * 0.38 + sin(vertical * 4.0 + seed) * 0.09;
  }

  void main() {
    vec3 point = surface * 3.5;
    float drift = elapsed * 0.045;
    float cloud = turbulence(point + vec3(drift, -drift, 0.0));
    float flow = turbulence(point + vec3(cloud, drift, cloud) * 1.4);
    float depth = max(surfaceNormal.z, 0.0);
    float rim = pow(1.0 - depth, 3.0);
    float wave = exp(-pow((surface.x - highlight) * 2.2, 2.0));
    vec3 tint = mix(accent, foreground, 0.2 + flow * 0.45);
    float opacity = 0.025 + flow * 0.055 + rim * 0.065 + wave * flow * 0.035;
    float cycle = mod(elapsed, 12.0) * 1.3;
    float seed = floor(elapsed / 12.0) + 2.0;
    float pulse = smoothstep(0.1, 0.4, cycle) * (1.0 - smoothstep(2.4, 3.4, cycle));
    float angle = hash(vec3(seed, 9.7, 3.1)) * 6.2831853;
    vec2 direction = vec2(cos(angle), sin(angle));
    vec2 boltSpace = vec2(dot(surface.xy, vec2(-direction.y, direction.x)), dot(surface.xy, direction));
    float front = mix(-0.9, 0.95, smoothstep(0.2, 2.1, cycle));
    float offset = sin(seed * 2.0) * 0.16;
    float path = lightningPath(boltSpace.y, seed) + offset;
    float distanceToBolt = abs(boltSpace.x - path);
    float growth = 1.0 - smoothstep(front - 0.04, front + 0.04, boltSpace.y);
    float core = (1.0 - smoothstep(0.002, 0.009, distanceToBolt)) * growth;
    float glow = exp(-distanceToBolt * 38.0) * growth;
    float illumination = exp(-pow(distanceToBolt / 0.16, 2.0)) * growth;
    for (int branchIndex = 0; branchIndex < 3; branchIndex++) {
      float branchNumber = float(branchIndex);
      float start = -0.48 + branchNumber * 0.3;
      float length = 0.55 + hash(vec3(seed, branchNumber, 5.0)) * 0.2;
      float side = branchIndex == 1 ? -1.0 : 1.0;
      float branchSeed = seed + 4.0 + branchNumber * 3.0;
      float travel = boltSpace.y - start;
      float branchPath = lightningPath(start, seed) + offset + side * travel * 1.05;
      branchPath += (lightningPath(boltSpace.y, branchSeed) - lightningPath(start, branchSeed)) * 0.45;
      float branchDistance = abs(boltSpace.x - branchPath);
      float branchFront = max(front - start, 0.0) / 1.4;
      float branchGrowth = 1.0 - smoothstep(branchFront - 0.03, branchFront + 0.03, travel);
      float branchExtent = smoothstep(0.0, 0.03, travel) * (1.0 - smoothstep(length - 0.08, length, travel));
      float width = mix(0.006, 0.0015, clamp(travel / length, 0.0, 1.0));
      core += (1.0 - smoothstep(width * 0.35, width, branchDistance)) * branchExtent * branchGrowth * 0.65;
      glow += exp(-branchDistance * 55.0) * branchExtent * branchGrowth * 0.35;
      illumination += exp(-pow(branchDistance / 0.11, 2.0)) * branchExtent * branchGrowth * 0.4;
    }
    float extent = smoothstep(-0.92, -0.8, boltSpace.y) * (1.0 - smoothstep(0.8, 0.92, boltSpace.y));
    illumination *= pulse * extent * stormEnabled * (0.45 + flow * 0.55);
    tint = mix(tint, vec3(0.035, 0.24, 0.85), min(illumination * 0.35, 0.35));
    opacity += illumination * 0.045;
    float lightning = (core * 0.5 + glow * 0.15) * pulse * extent * stormEnabled;
    tint = mix(tint, mix(vec3(0.02, 0.22, 1.0), foreground, core * 0.4), min(lightning * 3.0, 1.0));
    opacity += lightning * 0.6;
    opacity *= smoothstep(0.0, 0.12, depth);
    gl_FragColor = vec4(tint, opacity);
    #include <colorspace_fragment>
  }
`;

export default function AnnaIdentity() {
  const hostRef = useRef(null);
  const sceneRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    const container = sceneRef.current;
    const motionPreference = matchMedia("(prefers-reduced-motion: reduce)");
    let renderer;
    try {
      renderer = new WebGLRenderer({ alpha: true, antialias: true });
    } catch {
      return;
    }
    renderer.setPixelRatio(Math.min(devicePixelRatio || 1, 1.5));
    renderer.setClearColor(0, 0);
    renderer.domElement.setAttribute("aria-hidden", "true");
    container.appendChild(renderer.domElement);

    const theme = getComputedStyle(host);
    const scene = new Scene();
    const camera = new OrthographicCamera(-1.2, 1.2, 1.2, -1.2, 0.1, 10);
    camera.position.z = 3;
    const geometry = new SphereGeometry(1, 48, 32);
    const material = new ShaderMaterial({
      vertexShader,
      fragmentShader,
      transparent: true,
      depthWrite: false,
      uniforms: {
        elapsed: { value: 0 },
        highlight: { value: 0 },
        stormEnabled: { value: 1 },
        accent: { value: new Color(theme.getPropertyValue("--color-primary").trim()) },
        foreground: { value: new Color(theme.getPropertyValue("--color-text").trim()) },
      },
    });
    const sphere = new Mesh(geometry, material);
    scene.add(sphere);
    let frame;
    let elapsed = 0;
    let previousTime = null;
    let contextLost = false;

    const draw = () => {
      const phase = motionPreference.matches ? 0 : elapsed;
      const highlight = Math.sin(phase * 0.16) * 1.3;
      material.uniforms.elapsed.value = phase;
      material.uniforms.highlight.value = highlight;
      material.uniforms.stormEnabled.value = motionPreference.matches ? 0 : 1;
      sphere.rotation.y = phase * 0.025;
      host.style.setProperty("--identity-flow-x", `${50 + highlight * 60}%`);
      host.style.setProperty("--identity-flow-y", `${50 + Math.sin(phase * 0.11) * 20}%`);
      renderer.render(scene, camera);
    };

    const animate = (timestamp) => {
      if (previousTime !== null) elapsed += Math.min((timestamp - previousTime) / 1000, 0.1);
      previousTime = timestamp;
      draw();
      frame = requestAnimationFrame(animate);
    };

    const resume = () => {
      cancelAnimationFrame(frame);
      previousTime = null;
      if (contextLost) return;
      host.dataset.reducedMotion = String(motionPreference.matches);
      draw();
      if (!document.hidden && !motionPreference.matches) frame = requestAnimationFrame(animate);
    };

    const resize = () => {
      renderer.setSize(container.clientWidth, container.clientHeight);
      draw();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(container);
    resize();
    host.dataset.sceneReady = "true";
    resume();

    const loseContext = (event) => {
      event.preventDefault();
      contextLost = true;
      cancelAnimationFrame(frame);
      delete host.dataset.sceneReady;
    };
    renderer.domElement.addEventListener("webglcontextlost", loseContext);
    document.addEventListener("visibilitychange", resume);
    motionPreference.addEventListener("change", resume);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      document.removeEventListener("visibilitychange", resume);
      motionPreference.removeEventListener("change", resume);
      renderer.domElement.removeEventListener("webglcontextlost", loseContext);
      geometry.dispose();
      material.dispose();
      renderer.dispose();
      renderer.domElement.remove();
      delete host.dataset.sceneReady;
    };
  }, []);

  return (
    <div className="anna-identity" ref={hostRef}>
      <div className="anna-identity-scene" ref={sceneRef} aria-hidden="true" />
      <div className="anna-identity-copy">
        <h3>Anna</h3>
        <p>AI entity</p>
      </div>
    </div>
  );
}